import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { ReusableStreamProperty } from '@ipreo/muni-shared';
import { GameDayViewType } from '../../../state/data/state';
import { GameDayRootReducerState } from '../../../state/root.state';
import {
  CombineTranche,
  CombineAllTranches,
  SelectTranche
} from '../../../state/charts/actions';
import {
  SetGamedayViewType,
  FilterByLegends
} from '../../../state/data/actions';
import {
  getGamedayViewTypeState,
  getFullGamedayState
} from '../../../state/data/reducer';
import {
  BarChartData,
  BarChartLegend,
  BarGroupData
} from '../../../shared/models/charts/chart-data-types';
import { getCorrectedSelectedTrancheId } from '../../../tranche/mappers/tranche.mapper';
import { getChartData } from '../../mappers/bar-chart.mapper';
import { TrancheGroup } from '../../data/gameDay-data.transformations';

@Injectable()
export class ProgressBarChartService {
  public selectedTrancheId: string;
  public trancheGroups: TrancheGroup[];
  public barGroups: BarGroupData[];
  public legendFilters: string[];
  public legends: BarChartLegend[];
  public isAllTranchesCombined: boolean;

  @ReusableStreamProperty
  public readonly chartData: Observable<BarChartData> = this.store.pipe(
    select(getFullGamedayState),
    filter(({ data: { deal } }) => !!deal),
    tap(
      ({
        tranches,
        data: {
          filters: { legendCategories }
        },
        charts: { selectedTrancheId, isAllTranchesCombined }
      }) => {
        this.trancheGroups = tranches.searched.trancheGroups;
        this.selectedTrancheId = selectedTrancheId;
        this.legendFilters = [...legendCategories];
        this.isAllTranchesCombined = isAllTranchesCombined;
      }
    ),
    map(getChartData),
    tap((data) => {
      this.legends = [...data.legend];
      this.barGroups = [...data.groups];
    })
  );

  @ReusableStreamProperty
  public readonly viewType: Observable<GameDayViewType> = this.store.pipe(
    select(getGamedayViewTypeState)
  );

  constructor(private store: Store<GameDayRootReducerState>) {}

  public setViewType(viewType: GameDayViewType): void {
    this.showAllLegendFilter();
    this.store.dispatch(new SetGamedayViewType(viewType));
  }

  handleEvents(event: { type: string; payload: string | string[] }): void {
    let combinedTranchesIds;
    switch (event.type) {
      case 'bar-click':
        this.selectTranche(event.payload as string);
        break;
      case 'cross-click':
        this.crossBtnClick();
        break;
      case 'combine-click':
        combinedTranchesIds = event.payload as string[];
        this.combineAllTranches(combinedTranchesIds);
        break;
      case 'legend-click':
        this.selectLegendFilter(event.payload as string);
        break;
      case 'showAll-click':
        this.showAllLegendFilter();
        break;
      default:
        break;
    }
  }

  public crossBtnClick(): void {
    this.store.dispatch(new SelectTranche(undefined));
  }

  public combineAllTranches(combinedTranchesIds: string[]): void {
    this.checkTranchesCombinedChange(combinedTranchesIds);
    this.combineTranche(combinedTranchesIds);
  }

  public checkTranchesCombinedChange(combinedTranchesIds: string[]): void {
    if (!this.barGroups) {
      return;
    }

    const combinedTranchesIdsObject: {
      [id: string]: string;
    } = combinedTranchesIds.reduce((prev, id) => ({ ...prev, [id]: id }), {});
    const isCombined: boolean = this.barGroups
      .filter((group) => !(group.id === combinedTranchesIdsObject[group.id]))
      .every((group) => group.combined || group.barsCount === 1);

    if (isCombined && !this.isAllTranchesCombined) {
      this.store.dispatch(new CombineAllTranches(!this.isAllTranchesCombined));
    } else if (combinedTranchesIds.length === 0 && this.isAllTranchesCombined) {
      this.store.dispatch(new CombineAllTranches(!this.isAllTranchesCombined));
    }
  }

  public combineTranche(combinedTranchesIds: string[]): void {
    this.store.dispatch(new CombineTranche(combinedTranchesIds));

    const selectedTrancheId = getCorrectedSelectedTrancheId(
      this.selectedTrancheId,
      combinedTranchesIds,
      this.trancheGroups
    );

    this.store.dispatch(new SelectTranche(selectedTrancheId));
  }

  public selectTranche(trancheId: string): void {
    this.store.dispatch(new SelectTranche(trancheId));
  }

  public showAllLegendFilter(): void {
    this.legendFilters = [];
    this.store.dispatch(new FilterByLegends(this.legendFilters));
  }

  public selectLegendFilter(legendId: string): void {
    if (this.isInDefaultState(this.legends) && this.legends.length > 1) {
      this.legendFilters = this.legends
        .filter(({ id }) => !this.selectedLegend({ id }, legendId))
        .map(({ id }) => id);
    } else {
      this.legendFilters = this.legends
        .filter(({ id, selected }: BarChartLegend) =>
          this.exceptSelectedLegends({ id, selected }, legendId)
        )
        .map(({ id }: { id: string }) => id);
    }

    this.store.dispatch(new FilterByLegends(this.legendFilters));
  }

  private isInDefaultState(legend: BarChartLegend[]): boolean {
    return legend.every((elem) => elem.selected === true);
  }

  private exceptSelectedLegends(
    { id, selected }: { id: string; selected: boolean },
    selectedId: string
  ) {
    return (
      (!!selected && this.selectedLegend({ id }, selectedId)) ||
      !(!!selected || this.selectedLegend({ id }, selectedId))
    );
  }

  private selectedLegend({ id }: { id: string }, selectedId: string) {
    return selectedId === id;
  }
}
